<template>
  <div>
    <div class="tw:mx-4 tw:mb-6">
      <FilterSelect
        id="smart-boost-history-commission-filter"
        class="tw:w-[14%] tw:min-w-[200px]"
        :options="commissionOptions"
        @select="onCommissionSelect"
        name="commissionLevel"
        :label="$t('rc_smart_boost_commission_menu')"
        :disabled="false"
        :withTranslations="true"
        :value="selectedOption"
        :valueLabel="selectedOption.id === 'all' ? $t(selectedOption?.label) : selectedOption?.label"
      />
    </div>
    <SmartBoostHistoryTable
      :columns="columns"
      :data="tableData"
      :isLoading="isBoostsHistoryFetching || isBoostsHistoryLoading"
    />
  </div>
</template>
<script setup>
import { computed, ref } from 'vue'

import FilterSelect from '@/components/base/filters/FilterSelect.vue'
import SmartBoostHistoryTable from '@/components/rateConnect/smartBoost/SmartBoostHistoryTable.vue'

import { useBoostLevels, useBoostsHistory } from '../queries'
import { selectedAccommodationId } from '@/layouts/queries'
import { useFormatting } from '@/composables/useFormatting'

const selectedCommissionFilter = ref('all')
const { boostLevels } = useBoostLevels()
const { formatDate, formatPercentage } = useFormatting()
const { boostsHistory, isBoostsHistoryLoading, isBoostsHistoryFetching } = useBoostsHistory(
  selectedAccommodationId,
  selectedCommissionFilter
)

const allOption = [{ id: 'all', value: '', label: 'rc_smart_boost_commission_menu_all' }]
const levels = computed(() =>
  boostLevels.value?.map(item => {
    return {
      id: item,
      value: item,
      label: formatPercentage(item, 0, 0),
    }
  })
)
const commissionOptions = computed(() => [...allOption, ...(levels.value || [])])
const selectedOption = computed(() => commissionOptions.value.find(item => item.id === selectedCommissionFilter.value))

const onCommissionSelect = commission => {
  selectedCommissionFilter.value = commission.option.id
}
const columns = [
  {
    id: 'createdAt',
    header: 'rc_smartboost_active_from',
    colType: 'date',
    sortable: true,
    className: 'col-createdAt',
  },
  {
    id: 'validUntil',
    header: 'rc_smartboost_active_until',
    colType: 'date',
    sortable: true,
    className: 'col-validUntil',
  },
  {
    id: 'deactivationReason',
    header: 'rc_smartboost_status',
    colType: 'string',
    sortable: true,
    className: 'col-deactivationReason',
  },
  {
    id: 'boostedDates',
    header: 'rc_smartboost_dates_boosted',
    colType: 'string',
    sortable: true,
    className: 'col-boostedDates',
  },
  {
    id: 'commission',
    header: 'rc_smartboost_history_commission',
    colType: 'percent',
    sortable: true,
    className: 'col-commission',
  },
]

const formatDateTime = date => {
  return formatDate(date, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const mapBoostStatus = item => {
  if (item.isActive) {
    return 'rc_provider_status_active'
  }

  switch (item.deactivationReason) {
    case 'Expired':
      return 'rc_smartboost_expired'
    case 'Deleted':
      return 'rc_smartboost_canceled'
    case 'Replaced':
      return 'rc_smartboost_commission_updated'
    case 'Campaign Altered':
      return 'rc_smartboost_base_commission_updated'
    default:
      return null
  }
}

const tableData = computed(() => {
  return (boostsHistory.value ?? [])
    .map(item => {
      const status = mapBoostStatus(item)
      if (!status) return null

      return {
        createdAt: formatDateTime(item.createdAt),
        validUntil: formatDateTime(item.validUntil),
        deactivationReason: status,
        boostedDates:
          item.startDate === item.endDate
            ? formatDate(item.startDate, { day: '2-digit', month: '2-digit', year: 'numeric' })
            : `${formatDate(item.startDate, { day: '2-digit', month: '2-digit', year: 'numeric' })} - ${formatDate(item.endDate, { day: '2-digit', month: '2-digit', year: 'numeric' })}`,
        commission: formatPercentage(item.boostCommissionLevel, 0, 0),
      }
    })
    .filter(Boolean)
})
</script>
